import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, ShieldCheck, Loader2 } from "lucide-react";
import api from "../../lib/api";

/**
 * OtpInput — 6 digit box with auto-advance, backspace, paste support.
 * Props: value, onChange, onComplete, disabled, testIdPrefix
 */
export function OtpInput({ value = "", onChange, onComplete, disabled, testIdPrefix = "otp" }) {
  const refs = useRef([]);
  const chars = value.padEnd(6, " ").split("").slice(0, 6);

  const set = (i, ch) => {
    const arr = value.padEnd(6, " ").split("");
    arr[i] = ch;
    const next = arr.join("").replace(/\s+$/g, "");
    onChange(next);
    if (ch && i < 5) refs.current[i + 1]?.focus();
    if (next.length === 6 && !next.includes(" ")) onComplete?.(next);
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !chars[i].trim() && i > 0) refs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const t = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (t) {
      e.preventDefault();
      onChange(t);
      if (t.length === 6) onComplete?.(t);
    }
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={onPaste}>
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`${testIdPrefix}-${i}`}
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={c.trim()}
          onChange={(e) => set(i, e.target.value.replace(/\D/, "").slice(-1))}
          onKeyDown={(e) => onKey(i, e)}
          className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl tabular bg-[#0F0F14] border border-white/10 focus:border-[#7000FF] focus:outline-none focus:ring-2 focus:ring-[#7000FF]/30 rounded-lg text-white transition-colors"
        />
      ))}
    </div>
  );
}

/**
 * OtpVerifyStep — one component reused by Register + ForgotPassword.
 * Props:
 *  - email, purpose ("register" | "reset")
 *  - onVerified(payload) — called on success
 *  - onBack()
 */
export default function OtpVerifyStep({ email, purpose, onVerified, onBack }) {
  const [code, setCode] = useState("");
  const [expiresIn, setExpiresIn] = useState(600);
  const [resendIn, setResendIn] = useState(60);
  const [resendsRemaining, setResendsRemaining] = useState(5);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/otp/status", { params: { email, purpose } });
      if (data.pending) {
        setExpiresIn(data.expires_in);
        setResendIn(data.resend_in);
        setResendsRemaining(data.resends_remaining);
      }
    } catch { /* silent */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, [email, purpose]);

  useEffect(() => {
    const id = setInterval(() => {
      setExpiresIn((s) => Math.max(0, s - 1));
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const verify = async (c) => {
    if (busy) return;
    const value = c || code;
    if (value.length !== 6) return toast.error("Enter all 6 digits");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { email, code: value, purpose });
      onVerified(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Verification failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/otp/resend", { email, purpose });
      toast.success("A new code was sent to your email");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally { setResending(false); }
  };

  const mm = String(Math.floor(expiresIn / 60)).padStart(2, "0");
  const ss = String(expiresIn % 60).padStart(2, "0");

  return (
    <div className="space-y-6" data-testid="otp-verify-step">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#7000FF]/15 border border-[#7000FF]/30 mb-4">
          <ShieldCheck className="w-6 h-6 text-[#7000FF]" />
        </div>
        <h3 className="text-white text-lg font-medium">Enter verification code</h3>
        <p className="text-sm text-gray-400 mt-2">
          We sent a 6-digit code to<br />
          <span className="text-white font-medium">{email}</span>
        </p>
      </div>

      <OtpInput value={code} onChange={setCode} onComplete={verify} disabled={busy} />

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          Code expires in <span className="tabular text-[#00F0FF]" data-testid="otp-countdown">{mm}:{ss}</span>
        </span>
        <span>Attempts left: 5</span>
      </div>

      <button
        data-testid="otp-verify-btn"
        onClick={() => verify()}
        disabled={busy || code.length !== 6}
        className="btn-primary w-full py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Verify Code"}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          data-testid="otp-back-btn"
          onClick={onBack}
          className="flex items-center gap-1 text-gray-400 hover:text-white text-xs">
          <ArrowLeft size={14} /> Back
        </button>
        <button
          data-testid="otp-resend-btn"
          onClick={resend}
          disabled={resendIn > 0 || resending || resendsRemaining <= 0}
          className="flex items-center gap-1 text-[#00F0FF] hover:text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed">
          <RefreshCw size={14} className={resending ? "animate-spin" : ""} />
          {resendIn > 0 ? `Resend in ${resendIn}s` : `Resend code (${resendsRemaining} left)`}
        </button>
      </div>
    </div>
  );
}
